/**
 * Hospital domain API response types.
 * (Moved from `src/shared/types/type/hospital.ts`)
 */

export type HospitalOriginType = {
  fullAddress: string
  id: number
  name: string
  tel: string
  location: {
    latitude: number
    longitude: number
  }
  weeklySchedules: PartnerHospitalSchedule[]
  todaySchedule?: PartnerHospitalSchedule
}

export type GetHospitalInfoResponseType = {
  id: number
  /** 구버전 API에는 없을 수 있다. 원장 상태가 CLOSED일 때만 true다. */
  closed?: boolean
  suspended?: boolean
  homepageUrl?: string
  hospital: {
    origin: HospitalOriginType
    translation: HospitalOriginType
    primaryTreatment: {
      treatmentId: number
      treatment: string
    }
    // 백엔드가 검색 색인과 같은 매핑 체인으로 내려주는 카테고리.
    // 구버전 백엔드에선 없을 수 있어 optional — 없으면 관련병원 모듈이 검색 lookup으로 폴백.
    regionCategoryIds?: number[]
    treatmentCategoryIds?: number[]
  }
  partnerHospitalOperation?: PartnerHospitalOperation
  // API 선배포 동안 구 응답도 받을 수 있도록 optional로 둔다.
  homepageDoctors?: HospitalHomepageDoctor[]
  hiraDoctorDepartments?: HospitalHiraDoctorDepartment[]
  // 백엔드가 미입점 병원에만 채우는 수집 정보. 입점 병원은 빈 목록이다.
  homepageEquipments?: HospitalHomepageEquipment[]
  hiraEquipments?: HospitalHiraEquipment[]
  homepageNonpayItems?: HospitalNonpayItem[]
  careFlags?: HospitalCareFlags
  // 미수집이면 응답에서 아예 빠진다. 값이 없다는 것은 "주차 불가"가 아니라 "확인된 정보 없음"이다.
  parking?: HospitalParking
  favorite: boolean
  partnerHospital: boolean
  /** 입점 병원이 직접 등록한 장비. 미입점 병원에서는 비어 있다. */
  equipments: PartnerHospitalEquipment[]
}

export type HospitalHomepageEquipment = {
  equipmentName: string
  sourceUrl?: string
  sortOrder?: number
}

export type HospitalHiraEquipment = {
  equipmentName: string
  equipmentCount?: number
}

export type HospitalNonpayItem = {
  treatmentName: string
  priceText: string
  note?: string
  sourceUrl?: string
}

export type HospitalParking = {
  /** true=가능, false=불가, null=원문은 있으나 판단 불가. */
  parkingAvailable?: boolean | null
  rawText: string
  sourceUrl?: string
}

export type HospitalHomepageDoctor = {
  id: number
  name: string
  position: string
  specialty?: string
}

export type HospitalHiraDoctorDepartment = {
  departmentCode: string
  departmentName: string
  doctorCount: number
  specialistCount: number
}

export type PartnerHospitalOperation = {
  hospitalOriginId: number
  mainImages: PartnerHospitalMainImage[]
  detailImages: PartnerHospitalDetailImage[]
  parkingInformation: string
  tags: PartnerHospitalTagCode[]
  primaryTreatment: string
  reviewCount: number
  introduction: {
    thumbnailUrl: string
    introduction: string
  }
  doctors: PartnerHospitalDoctor[]
  equipmentOverview: {
    equipmentFileId: number
    equipmentThumbnailUrl: string
    equipmentDescription: string
  }
  equipments: PartnerHospitalEquipment[]
  uninsuredItems: PartnerHospitalUninsuredItem[]
}

export type PartnerHospitalMainImage = {
  id: number
  url: string
  displayOrder: number
}

export enum PartnerHospitalTagCode {
  NIGHT_CARE = "NIGHT_CARE",
  HOLIDAY_CARE = "HOLIDAY_CARE",
  EMERGENCY_CARE = "EMERGENCY_CARE",
}

export type HospitalCareFlags = {
  nightCare: boolean
  saturdayCare: boolean
  sundayCare: boolean
  holidayCare: boolean
}

export type PartnerHospitalDetailImage = {
  id: number
  url: string
}

export type PartnerHospitalSchedule = {
  dayType: string
  startTime: string
  endTime: string
}

export enum PartnerHospitalDoctorPositionCode {
  CHIEF_DIRECTOR = "CHIEF_DIRECTOR",
  DIRECTOR = "DIRECTOR",
  DOCTOR = "DOCTOR",
}

export type PartnerHospitalDoctor = {
  doctorId: number
  profileImageUrl: string
  name: string
  positionCode: PartnerHospitalDoctorPositionCode
  specialtyKeywords: string
  careers: number | null
  careerDescription: string[] | null
  sortOrder: number
  /** 공개 API는 주요 진료가 없으면 []를 반환하며, 화면은 구 응답의 빈 값도 방어한다. */
  majorTreatments: string[]
}

export type PartnerHospitalEquipment = {
  equipmentId: number
  equipmentName: string
  equipmentCount: number
}

export type PartnerHospitalUninsuredItem = {
  id: number
  itemName: string
  priceText: string
}
